import React, { useEffect, useState } from 'react';

import Modal from '../common/Modal';
// Import the CSS file for styling

import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';
import { validateRequiredFields } from "../../utils/formValidation";
import { deepTrim } from "../../utils/formValidation";

const WaCategoryPage = () => {

  const waApi = useWaApi();
  // Only a SYSTEM_ADMIN or ADMIN may change anything here; a USER gets a read-only view. The service refuses
  // the writes too, so this just keeps the controls out of sight.
  const { canManageWhatsAppSetup } = useUserProfileAccess();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false); // State for modal visibility
  const [newCategory, setNewCategory] = useState({
    templateCategory: '',
    status: false,
  });

  // State for filters
    const initialFilters = {
      id: null,
      templateCategory: null,
      status: null
    };
    const [filters, setFilters] = useState(initialFilters);
    
    const resetFilters = () => {
      clearMessages();
      setFilters(initialFilters);
    };

  //for handling pagination
  const [page, setPage] = useState(0); // Starting from the first page
  const [size, setSize] = useState(3); // Number of items per page
  const [totalPages, setTotalPages] = useState(0); // To store total pages
  const [formError, setFormError] = useState({});
  const [actionErrors, setActionErrors] = useState({});
  const [createError, setCreateError] = useState(null);
  const [createMessage, setCreateMessage] = useState(null);

  const clearMessages = () => {
    setCreateError(null);
    setCreateMessage(null);
    setActionErrors({});
    setFormError({});
  };

  useEffect(() => {

    const fetchCategories = async () => {
      try {
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_CATEGORIES_BY_PAGE_ENDPOINT,
          {
            params: {
              ...filters,
              page,
              size,
            },
          }
        );

        // Extract data from the response
        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) {
          setCategories(object.content); // Set the categories from the response object
          setTotalPages(object.totalPages);
        } else {
          setError(`Error: ${message}`); // Handle non-success status code
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError(err.message || 'An error occurred.');
        setLoading(false);
      }
    };

    fetchCategories();
  }, [waApi, page, size]);
  
  useEffect(() => {
    if (modalOpen) {
      setFormError({});
    }
  }, [modalOpen]);
  
  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;

    setNewCategory((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value, // Use checked for checkbox type
    }));
  };

  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const newFilters = { ...prev, [name]: value };
      return newFilters;
    });
  };

  // Handle the status change to convert to boolean
  const handleStatusChange = (e) => {
    const value = e.target.value;
    const statusValue = value === 'Active' ? true : value === 'Inactive' ? false : null;

    setFilters((prev) => {
      const newFilters = { ...prev, status: statusValue };
      return newFilters;
    });
  };


  const refreshCache = async () => {
    clearMessages();
    try {
      const response = await waApi.get(
        process.env.REACT_APP_REFRESH_WA_CATEGORY_CACHE_ENDPOINT
      );
      if (response.data.httpStatus === 200) {
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while refreshing the cache.');
    }
  };

  const toggleActivationStatus = async (categoryId, currentStatus) => {
    clearMessages();
    try {
      const newStatus = !currentStatus; // Toggle the current status
      const response = await waApi.patch(
        `${process.env.REACT_APP_SET_WA_CATEGORY_STATUS_ENDPOINT}/${categoryId}`,
        { status: newStatus }
      );
      setCategories((prev) =>
        prev.map((category) => category.id === categoryId ? { ...category, status: newStatus } : category)
      );
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [categoryId]: err.message || 'An error occurred while updating the status.' }));
    }
  };

  const handleSubmit = async (e) => {

    e.preventDefault();
    clearMessages();
    const validation = validateRequiredFields({
        templateCategory: {
          value: newCategory.templateCategory,
          label: "Template Category"
        }
    });
    let errors = { ...validation.errors };
        if (Object.keys(errors).length > 0) {
      setFormError(errors);
      return;
    }
    try {
      const payload = deepTrim(newCategory);
      const response = await waApi.post(
        `${process.env.REACT_APP_SET_WA_CATEGORY_ENDPOINT}`,
        payload
      );
      if (response.data.httpStatus === 200) {
        setCategories((prev) => [...prev, response.data.object]); // Update with new category
        setNewCategory({ templateCategory: '', status: true }); // Reset form
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while creating the category.');
    } finally {
      setModalOpen(false);
    }
  };

  // New function to apply filters
  const applyFilters = async (pageParam) => {

    setLoading(true); // Show loading while fetching filtered notifications
    clearMessages();
    var hitApplyFilter = null;
    if(page === 0) {
      hitApplyFilter = true;
    } else {
      hitApplyFilter = false;
    }
    if(pageParam===0) {
      setPage(0);
    }
    if(hitApplyFilter) {
      try {
        const cleanedFilters = Object.fromEntries(
          Object.entries(filters).map(([key, value]) => [
            key,
            value === '' ? null : value,
          ])
        );
      const response = await waApi.get(
        process.env
          .REACT_APP_GET_ALL_WA_CATEGORIES_BY_PAGE_ENDPOINT,
        {
          params: {
            ...cleanedFilters,
            page,
            size,
          },
        }
      );

      // Extract data from the response
      const { message, object, httpStatus } = response.data;

      if (httpStatus === 200) {
        setCategories(object.content); // Set the categories from the response object
        setTotalPages(object.totalPages);
      } else {
        setCreateError(message); // Handle non-success status code
      }

      setLoading(false);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCreateError(err.message || 'An error occurred.');
        setLoading(false);
      }
    }
  };

  const handleNextPage = () => {
    clearMessages();
    if (page + 1 < totalPages) {
      setPage((prevPage) => prevPage + 1); // Increment page number
    }
  };

  const handlePreviousPage = () => {
    clearMessages();
    if (page > 0) {
      setPage((prevPage) => prevPage - 1); // Decrement page number
    }
  };

  const handleSizeChange = (e) => {
    clearMessages();
    setSize(e.target.value);
    setPage(0); // Reset to first page on changing size
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading categories: {error}</div>;

  return (
    <div className="page-container">
      <h1>App Templates Categories</h1>
      <div className="flex-row filter-section">
        <div className='filter-item'>
          <label htmlFor="items-per-page">Items per page: </label>
          <select id="items-per-page" onChange={handleSizeChange} className="w-auto">
            <option value={3}>default</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </div>
      </div>
      <h3>Filters</h3>
      {/* Filter Form */}
      <div className="flex-row filter-section">
        <div className="filter-item">
          <label>Id:</label>
          <input
            type="text"
            name="id"
            value={filters.id ?? ''}
            onChange={handleFilterInputChange}
            placeholder="Only numeric value"
          />
        </div>

        <div className="filter-item">
          <label>TemplateCategory:</label>
          <input
            type="text"
            name="templateCategory"
            value={filters.templateCategory ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Status:</label>
          <select name="status" onChange={handleStatusChange} value={filters.status === null ? '' : filters.status ? 'Active' : 'Inactive'}>
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <button type="button" onClick={() => applyFilters(0)}>Apply Filters</button>
        <button type="button" onClick={resetFilters}> Reset Filters</button>

      </div>

      {/* Button to open the modal */}
      {canManageWhatsAppSetup && (
        <div className="action-bar">
          <button
            onClick={() => {
              clearMessages();
              setNewCategory({ templateCategory: '', status: false });
              setModalOpen(true);
            }}
          >
            + Create New
          </button>

          <button type="button" onClick={refreshCache}>
            Refresh Cache
          </button>
        </div>
      )}

      {createMessage && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createMessage}
        </div>
      )}

      {createError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createError}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => { clearMessages(); setModalOpen(false); }}>
        <div className="page-container">
           {formError && Object.values(formError).some(val => val !== null && val !== undefined && val !== "") && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
                  <strong>Please fix the following errors:</strong>
                  <ul style={{ margin: "8px 0 0 20px" }}>
                    {Object.entries(formError)
                      .filter(([_, val]) => val !== null && val !== undefined && val !== "")
                      .map(([key, err], index) => (
                        <li key={index}>
                          {typeof err === "object" ? JSON.stringify(err) : String(err)}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
          <form onSubmit={handleSubmit} className="standard-form">
            <h3>Create New Template Category</h3>
            <div className="form-group">
              <label>Template Category:</label>
              <input
                type="text"
                name="templateCategory"
                value={newCategory.templateCategory}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Activation Status:</label>
              <input
                type="checkbox"
                name="status"
                checked={newCategory.status}
                onChange={handleInputChange}
              />
            </div>
            <button type="submit">Create Category</button>
          </form>
        </div>
      </Modal>

      <h3>Existing App Templates Categories</h3>
      {categories.length === 0 ? (
        <p>No categories available</p>
      ) : (
        <div className="table-responsive">
          <table className="notifications-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Category Name</th>
                <th>Created At</th>
                <th>Status</th>
                <th>Last Updated At</th>
                <th>Last Updated By</th>
                {canManageWhatsAppSetup && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {categories?.map((category) => (
                <tr key={category.id}>
                  <td>{category.id}</td>
                  <td>{category.templateCategory}</td>
                  <td>{new Date(category.createdTimestamp).toLocaleString()}</td>
                  <td>{category.status ? 'Active' : 'Inactive'}</td>
                  <td>{new Date(category.updatedTimestamp).toLocaleString()}</td>
                  <td>{category.lastUpdatedBy}</td>
                  {canManageWhatsAppSetup && (
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => toggleActivationStatus(category.id, category.status)}>
                          {category.status ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                      {actionErrors[category.id] && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                          {actionErrors[category.id]}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Pagination Controls */}
      <div className="pagination-container">
        <button onClick={handlePreviousPage} disabled={page === 0}>Previous</button>
        <span>Page {page + 1} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={page + 1 === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default WaCategoryPage;
